import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Activity, CheckCircle2, AlertCircle, Cloud, Database, MessageSquare,
  PhoneCall, Mail, Lock, Sparkles, ArrowRight, ArrowLeft, Clock,
} from "lucide-react";
import { FinalCTA } from "@/components/landing/FinalCTA";

/* /status — system health + recent incidents. Static (mock) for now. */

const SYSTEMS = [
  { Icon: Cloud,          name: "Web app",         status: "operational", uptime: "99.99%" },
  { Icon: Database,       name: "API · backend",   status: "operational", uptime: "99.97%" },
  { Icon: Activity,       name: "EMR sync (Jane / Juvonno)", status: "operational", uptime: "99.95%" },
  { Icon: Sparkles,       name: "KAI · AI workforce",        status: "operational", uptime: "99.92%" },
  { Icon: PhoneCall,      name: "Voice agents",    status: "operational", uptime: "99.93%" },
  { Icon: MessageSquare,  name: "Unified inbox",   status: "operational", uptime: "99.99%" },
  { Icon: Mail,           name: "Transactional email",      status: "operational", uptime: "99.99%" },
  { Icon: Lock,           name: "Authentication · SSO",     status: "operational", uptime: "100.0%" },
];

const INCIDENTS = [
  {
    date: "Feb 8, 2026",
    title: "Resolved · Brief KAI latency spike (12 min)",
    body: "Increased response time on KAI chat between 14:02–14:14 ET due to upstream model provider. Auto-failover restored normal latency. No data loss.",
    severity: "minor",
  },
  {
    date: "Jan 24, 2026",
    title: "Resolved · Juvonno sync delay",
    body: "Bidirectional sync with Juvonno paused for 22 minutes during a Juvonno-side maintenance window. All queued events replayed on resume.",
    severity: "minor",
  },
  {
    date: "Jan 11, 2026",
    title: "Resolved · Scheduled maintenance",
    body: "Routine maintenance window for database upgrade. Read-only access maintained throughout.",
    severity: "maintenance",
  },
];

const SEV = {
  operational:  { dot: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-300", label: "Operational" },
  degraded:     { dot: "bg-amber-500",   text: "text-amber-600 dark:text-amber-300",     label: "Degraded" },
  outage:       { dot: "bg-rose-500",    text: "text-rose-600 dark:text-rose-300",       label: "Outage" },
};

const INC_BADGE = {
  minor:       "border-amber-500/30 bg-amber-500/[0.08] text-amber-600 dark:text-amber-300",
  major:       "border-rose-500/30 bg-rose-500/[0.08] text-rose-600 dark:text-rose-300",
  maintenance: "border-sky-500/30 bg-sky-500/[0.08] text-sky-600 dark:text-sky-300",
};

export default function Status() {
  return (
    <article data-testid="status-page" className="overflow-hidden">

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0a0a14] text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-50"
          style={{ background: "radial-gradient(55% 70% at 75% 0%, rgba(16,185,129,0.25), transparent 70%), radial-gradient(45% 55% at 15% 80%, rgba(14,165,233,0.16), transparent 70%)" }}
        />
        <div className="relative mx-auto max-w-5xl px-6 pt-32 pb-12 sm:px-10 sm:pt-40 sm:pb-16 lg:px-16">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.32em] text-white/60">
            System status
          </p>
          <h1 className="mt-4 font-display text-4xl font-black leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
            All systems{" "}
            <span className="bg-gradient-to-r from-emerald-300 to-teal-300 bg-clip-text italic text-transparent">
              operational.
            </span>
          </h1>

          {/* Live indicator */}
          <div className="mt-7 inline-flex items-center gap-2.5 rounded-full border border-emerald-400/30 bg-emerald-500/[0.1] px-4 py-2 backdrop-blur-sm">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            <span className="text-[12px] font-semibold text-emerald-200">
              Live · updated continuously
            </span>
          </div>

          <p className="mt-6 max-w-xl text-[14.5px] leading-relaxed text-white/70">
            Real-time health for every Clinic OS module — plus a log of recent incidents and maintenance.
          </p>
        </div>
      </section>

      {/* SYSTEM CARDS */}
      <section className="border-t border-border py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6 sm:px-10 lg:px-16">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-[10.5px] font-bold uppercase tracking-[0.28em] text-emerald-600 dark:text-emerald-400">
                Component status
              </p>
              <h2 className="mt-2 font-display text-2xl font-black tracking-tight sm:text-3xl">
                Eight services. <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text italic text-transparent">All green.</span>
              </h2>
            </div>
            <p className="hidden text-[11px] uppercase tracking-[0.18em] text-muted-foreground sm:block">
              90-day uptime
            </p>
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {SYSTEMS.map((s, i) => {
              const sev = SEV[s.status];
              return (
                <motion.div
                  key={s.name}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.3, delay: i * 0.03 }}
                  data-testid={`status-system-${i}`}
                  className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4"
                >
                  <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-emerald-500/12 text-emerald-600 dark:text-emerald-300">
                    <s.Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-[14px] font-bold leading-tight tracking-tight">{s.name}</p>
                    <p className={`mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-semibold ${sev.text}`}>
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${sev.dot}`} />
                      {sev.label}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                      90-day
                    </p>
                    <p className="font-display text-[15px] font-black tracking-tight">{s.uptime}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INCIDENT HISTORY */}
      <section className="border-t border-border bg-secondary/30 py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-6 sm:px-10 lg:px-16">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.28em] text-muted-foreground">
            Recent incidents · last 90 days
          </p>
          <h2 className="mt-2 font-display text-2xl font-black tracking-tight sm:text-3xl">
            What happened, when, and why.
          </h2>

          <ol className="mt-8 space-y-3">
            {INCIDENTS.map((inc, i) => (
              <motion.li
                key={inc.title}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
                data-testid={`status-incident-${i}`}
                className="rounded-2xl border border-border bg-card p-5"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                    <Clock className="h-3 w-3" /> {inc.date}
                  </span>
                  <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ${INC_BADGE[inc.severity]}`}>
                    {inc.severity}
                  </span>
                </div>
                <p className="mt-2 font-display text-[15px] font-bold leading-snug tracking-tight">
                  <CheckCircle2 className="mr-1.5 inline-block h-4 w-4 align-text-bottom text-emerald-500" />
                  {inc.title}
                </p>
                <p className="mt-1.5 text-[13.5px] leading-relaxed text-muted-foreground">
                  {inc.body}
                </p>
              </motion.li>
            ))}
          </ol>

          <div className="mt-10 flex items-center justify-between">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.22em] text-violet-600 hover:text-violet-700 dark:text-violet-300"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to home
            </Link>
            <a
              href="mailto:status@clinicfirst.app?subject=Subscribe%20to%20status%20updates"
              data-testid="status-subscribe"
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-card px-4 py-2 text-[12.5px] font-bold transition-colors hover:border-foreground/30 hover:bg-secondary"
            >
              <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
              Subscribe to incident alerts
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>

      <FinalCTA />
    </article>
  );
}
